import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Divider, Drawer, Typography, Upload, message } from "antd";
import { useState } from "react";
import CustomButton from "../common/CustomButton";
import OrgService from "../services/Org/OrgService";
import StockService from "../services/Stock/StockService";
import UserService from "../services/User/UserService";

const { Title, Text } = Typography;

const ImportDrawer = ({ open, onClose, type }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isCheckBtnDisabled, setIsCheckBtnDisabled] = useState(false);
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = useState(true);

  const handleUpload = (file) => {
    const isCSV = file.type === "text/csv";
    const isXLSX =
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isCSV && !isXLSX) {
      message.error("You can only upload CSV or XLSX files");
      return Upload.LIST_IGNORE;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("File must be smaller than 2MB");
      return Upload.LIST_IGNORE;
    }

    setUploadedFile(file);
    setIsCheckBtnDisabled(false); // Enable "Check File"
    setIsSubmitBtnDisabled(true); // Disable "Submit File"
    message.success("File Uploaded Successfully");

    return false;
  };

  const downloadImportTemplate = () => {
    let downloadApi;
    switch (type) {
      case "SupplierImport":
        downloadApi = OrgService.downloadImportTemplate;
        break;
      case "UserImport":
        downloadApi = UserService.downloadImportTemplate;
        break;
      case "StockImport":
        downloadApi = StockService.downloadImportTemplate;
        break;
      default:
        message.error("Unknown type for downloading template");
        return;
    }

    downloadApi()
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const contentDisposition = response.headers["content-disposition"];
        const filename =
          contentDisposition?.match(/filename="(.+)"/)?.[1] ||
          "import-template.xlsx";
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Template downloaded successfully.");
      })
      .catch((error) => {
        console.error("Error exporting to Excel:", error);
        message.error("Failed to download template.");
      });
  };

  const handleSubmit = (isDryRun, variant) => {
    if (!uploadedFile) {
      message.error("Please upload a file first");
      return;
    }

    const fileBlob = new Blob([uploadedFile], { type: uploadedFile.type });
    const formData = new FormData();
    formData.append("file", fileBlob);
    formData.append("isDryRun", isDryRun);
    formData.append("variant", variant);

    let submitApi;
    switch (type) {
      case "SupplierImport":
        submitApi = OrgService.uploadOrgData;
        break;
      case "UserImport":
        submitApi = UserService.uploadUserData;
        break;
      case "StockImport":
        submitApi = StockService.uploadStockData;
        break;
      default:
        message.error("Unknown type for file submission");
        return;
    }

    submitApi(formData)
      .then((response) => {
        console.log("Response:", response.data);

        if (response.data.hasError) {
          message.error("Incorrect File");
          setIsCheckBtnDisabled(false); // Keep "Check File" enabled
          setIsSubmitBtnDisabled(true); // Keep "Submit File" disabled
        } else {
          message.success("File Checked Successfully");
          setIsCheckBtnDisabled(true); // Disable "Check File"
          setIsSubmitBtnDisabled(false); // Enable "Submit File"
          if (!isDryRun) {
            onClose();
          }
        }
      })
      .catch((error) => {
        console.error("Error importing file: ", error);
        message.error(error?.response?.data?.message || "Error importing file. Please try again.");
      });
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setIsCheckBtnDisabled(false); // Enable "Check File" when file is removed
    setIsSubmitBtnDisabled(true); // Disable "Submit File" when file is removed
    message.info("File removed successfully.");
  };

  const handleClose = () => {
    setUploadedFile(null);
    setIsCheckBtnDisabled(false);
    setIsSubmitBtnDisabled(true);
    onClose();
  };

  const renderPreview = () => {
    if (!uploadedFile) return null;

    return (
      <div className="mt-4 p-4 border rounded bg-gray-50">
        <Text strong>File Name:</Text> <Text>{uploadedFile.name}</Text>
        <div className="mt-3 flex justify-between items-center">
          <CustomButton
            type="tertiary"
            onClick={() => {
              window.open(URL.createObjectURL(uploadedFile));
            }}
          >
            Open File
          </CustomButton>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemoveFile}
          >
            Remove
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={<Title level={3} className="ml-4">Import Data</Title>}
      placement="right"
      onClose={handleClose}
      open={open}
      width={500}
      styles={{ body: { padding: 24 } }}
    >
      <Text>
        Upload a CSV or XLSX file. Ensure it follows the provided template.
      </Text>
      <Divider />

      <div className="flex justify-between mb-6">
        <Upload
          name="file"
          accept=".csv,.xlsx"
          beforeUpload={handleUpload}
          maxCount={1}
          showUploadList={false}
        >
          <Button type="primary" icon={<UploadOutlined />}>Upload File</Button>
        </Upload>
        <Button type="default" onClick={downloadImportTemplate}>
          Download Template
        </Button>
      </div>

      {renderPreview()}

      <Divider />
      <div className="flex justify-between mt-4">
        <Button
          onClick={() => handleSubmit(true, "test")}
          disabled={isCheckBtnDisabled}
        >
          Check File
        </Button>
        <Button
          type="primary"
          onClick={() => handleSubmit(false, "test")}
          disabled={isSubmitBtnDisabled}
        >
          Submit File
        </Button>
      </div>
    </Drawer>
  );
};

export default ImportDrawer;
